<template>
  <div class="global-container-home">

    <PublicHome v-if="!isConnected"/>

    <div v-else>
      <div class="bento-container" :class="{ 'single-column': hasSingleBentoItem }">

        <div v-if="haveNoOption" class="bento-item warning">
          <div>
            Votre espace entreprise ne dispose actuellement d’aucun module actif. Ce logiciel fonctionne par modules : vous n’accédez et ne payez que pour les fonctionnalités dont vous avez réellement besoin.
            <br><br>
            Pour ajouter des modules, cliquez sur « Paramètres » en haut à droite, puis sélectionnez <strong>Paramètres du groupe</strong>, et enfin <strong>Modules</strong>.
            Vous pouvez également cliquer <router-link to="/group-parameters" class="router-link">ici</router-link>.
          </div>
        </div>

        <div v-if="haveBusinessOption" class="bento-item">
          <div>
            <h3>Actions</h3>
            <div class="actions-container">
              <router-link to="/clients#add" class="router-link action-card">
                <span class="action-icon"><i class="icon fas fa-user-plus"></i></span>
                <p>Ajouter un client</p>
              </router-link>
              <router-link to="/businesses#add-quote" class="router-link action-card">
                <span class="action-icon"><i class="icon fas fa-file-circle-plus"></i></span>
                <p>Créer un devis</p>
              </router-link>
              <router-link :to="`/businesses/unpaid`" class="router-link action-card">
                <span v-if="overdueCount" class="action-badge" title="Factures échues à ce jour">{{ overdueCount }}</span>
                <span class="action-icon"><i class="icon fas fa-money-bill-transfer"></i></span>
                <p>Paiements en retard</p>
              </router-link>
            </div>
            <nav class="action-links" aria-label="Autres actions clients">
              <router-link to="/clients/prospection" class="router-link">Clients à prospecter</router-link>
              <router-link to="/clients/acquis" class="router-link">Clients acquis</router-link>
            </nav>
          </div>
        </div>

        <div v-if="haveBusinessOption" class="bento-item">
          <div>
            <h3>À faire</h3>
            <p class="todo-sub">Ce qui attend votre attention</p>
            <div v-if="todoFailed" class="todo-empty">Cette synthèse n'a pas pu être chargée.</div>
            <div v-else-if="!todo" class="todo-empty">Chargement…</div>
            <ul v-else class="todo-list">
              <li v-for="item in todoItems" :key="item.key">
                <router-link :to="item.to" class="router-link todo-item" :class="[item.tone, {'is-done': item.done}]">
                  <strong class="todo-value">{{ item.value }}</strong>
                  <span class="todo-text">
                    <span class="todo-label">{{ item.label }}</span>
                    <span class="todo-hint">{{ item.hint }}</span>
                  </span>
                </router-link>
              </li>
            </ul>
          </div>
        </div>

        <div v-if="haveStatisticsOption" class="bento-item">
          <div>
            <h3>Statistiques</h3>
            <p class="stats-sub">30 derniers jours, comparés aux 30 jours précédents</p>
            <div v-if="statsFailed" class="stats-empty">Les statistiques n'ont pas pu être chargées.</div>
            <div v-else-if="!stats" class="stats-empty">Chargement…</div>
            <template v-else>
              <div class="stats-grid">
                <div v-for="kpi in statsKpis" :key="kpi.label" class="stats-item">
                  <span class="stats-label">{{ kpi.label }}</span>
                  <strong class="stats-value">{{ kpi.value }}</strong>
                  <span v-if="kpi.hasGrowth" class="stats-growth" :class="kpi.growthClass">
                    {{ kpi.growthText }}
                    <span class="stats-tip">30 jours précédents : {{ kpi.previous }}</span>
                  </span>
                  <span v-else class="stats-hint">{{ hasPreviousActivity ? 'Rien sur les 30 jours précédents' : '\u00A0' }}</span>
                </div>
              </div>
              <p v-if="!hasPreviousActivity" class="stats-note">Les comparaisons arriveront au fur et à mesure de votre activité : dès que les 30 jours précédents auront de l'activité, vous verrez l'évolution de chaque indicateur.</p>
            </template>
            <router-link to="/statistics" class="router-link stats-link">Voir toutes les statistiques</router-link>
          </div>
        </div>

        <div v-if="haveBusinessOption" class="bento-item">
          <CustomLoader ref="loader"/>
          <div>
            <h3>Journal</h3>
            <div v-if="logData.length === 0">
              <ul>
                Pas de log
              </ul>
            </div>
            <div v-else class="notification-container">
              <div v-if="logsToday().length" class="section">
                <div class="header-section">Aujourd'hui</div>
                <div v-for="(log, index) in logsToday()" :key="index" class="notification">
                  <div v-if="log.type === 'SIGNATURE'" class="signature"><p>SIGNATURE</p></div>
                  <p class="number">{{ log.number }}</p>
                  <p class="currentClientName">{{ log.currentClientName }}</p>
                  <p class="signedAt">{{ timeAgo(new Date(log.signedAt)) }}</p>
                </div>
              </div>
              <div v-if="logsThisWeek().length" class="section">
                <div class="header-section">La semaine dernière</div>
                <div v-for="(log, index) in logsThisWeek()" :key="index" class="notification">
                  <div v-if="log.type === 'SIGNATURE'" class="signature"><p>SIGNATURE</p></div>
                  <p class="number">{{ log.number }}</p>
                  <p class="currentClientName">{{ log.currentClientName }}</p>
                  <p class="signedAt">{{ timeAgo(new Date(log.signedAt)) }}</p>
                </div>
              </div>
              <div v-if="logsOlder().length" class="section">
                <div class="header-section">Il y a plus d'un mois</div>
                <div v-for="(log, index) in logsOlder()" :key="index" class="notification">
                  <div v-if="log.type === 'SIGNATURE'" class="signature"><p>SIGNATURE</p></div>
                  <p class="number">{{ log.number }}</p>
                  <p class="currentClientName">{{ log.currentClientName }}</p>
                  <p class="signedAt">{{ timeAgo(new Date(log.signedAt)) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="bento-item">
          <div>
            <h3>Dernières évolutions</h3>
            <p class="changelog-intro">
              Citron évolue régulièrement pour améliorer la facturation électronique,
              la gestion des clients et la comptabilité.
            </p>
            <ul class="change-logs">
              <li><p>Comptabilité améliorée</p><p>Septembre 2026</p></li>
              <li><p>Facturation électronique</p><p>Août 2026</p></li>
              <li><p>Filtrage</p><p>1 Mars 2026</p></li>
              <li><p>Optimisation tableau</p><p>25 Mars 2026</p></li>
              <li><p>Design</p><p>24 Janvier 2026</p></li>
              <li><p>Production vers Dossiers</p><p>15 Janvier 2026</p></li>
            </ul>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import {faFileCirclePlus, faMoneyBillTransfer, faUserPlus, faSignature} from "@fortawesome/free-solid-svg-icons";
import {library} from "@fortawesome/fontawesome-svg-core";
import CustomLoader from "@/components/CustomLoader.vue";
import {BusinessController} from "@/controllers/business/BusinessController";
import {timeAgo} from "@/utils";
import { useHead } from '@vueuse/head'
import PublicHome from "@/components/public/PublicHome.vue";
import {StatisticsController} from "@/statistics/controllers/StatisticsController";
import {integer, money, percent} from "@/statistics/utils/format";
library.add(faUserPlus);
library.add(faMoneyBillTransfer);
library.add(faFileCirclePlus);
library.add(faSignature);

export default {
  name: 'HomeView',
  data() {
    return {
      logData: [],
      todo: undefined,
      todoFailed: false,
      stats: undefined,
      statsFailed: false,
    };
  },
  computed: {
    haveNoOption() {
      const raw = localStorage.getItem('options');
      if (!raw) return true;

      const options = raw.split(',').map(o => o.trim()).filter(Boolean);
      return options.length === 0;
    },
    haveBusinessOption() {
      const raw = localStorage.getItem('options');
      if (!raw) return false;

      const options = raw.split(',').map(o => o.trim()).filter(Boolean);
      return options.includes('business');
    },
    haveStatisticsOption() {
      const raw = localStorage.getItem('options');
      if (!raw) return false;

      const options = raw.split(',').map(o => o.trim()).filter(Boolean);
      return options.includes('statistics');
    },
    isConnected() {
      return localStorage.isConnected;
    },
    // Factures échues : affichées en pastille sur « Paiements en retard »
    overdueCount() {
      return this.todo?.overdueCount ?? 0;
    },
    // Performance des 30 derniers jours face aux 30 jours précédents : chiffre d'affaires, devis, prospection
    statsKpis() {
      const k = this.stats.kpis;
      const withGrowth = (label, kpi, format) => {
        const hasGrowth = kpi.growth !== undefined && kpi.growth !== null;
        return {
          label,
          value: format(kpi.value),
          previous: format(kpi.previous),
          hasGrowth,
          growthClass: !kpi.growth ? 'flat' : (kpi.growth > 0 ? 'good' : 'bad'),
          growthText: `${kpi.growth > 0 ? '▲' : kpi.growth < 0 ? '▼' : '='} ${percent(Math.abs(kpi.growth ?? 0))}`,
        };
      };
      return [
        withGrowth('CA facturé HT', k.invoicedHt, money),
        withGrowth('CA encaissé HT', k.collectedHt, money),
        withGrowth('Devis créés', k.quotesCreated, integer),
        withGrowth('Taux de signature', k.signatureRate, percent),
        withGrowth('Nouveaux clients', k.newClients, integer),
        withGrowth('Panier moyen HT', k.averageBasketHt, money),
      ];
    },
    // Sans activité le mois précédent, aucune comparaison n'est possible
    hasPreviousActivity() {
      return this.statsKpis.some(kpi => kpi.hasGrowth);
    },
    // Ce qu'il reste à faire, du plus urgent au moins urgent ; une ligne à zéro est affichée comme faite
    todoItems() {
      const t = this.todo;
      const plural = (count, singular, pluralForm) => count > 1 ? pluralForm : singular;
      const euros = amount => (amount ?? 0).toLocaleString('fr-FR', {style: 'currency', currency: 'EUR'});
      return [
        {
          key: 'overdue', to: '/businesses/unpaid', tone: 'bad', done: !t.overdueCount,
          value: euros(t.overdueAmount),
          label: 'Impayés échus',
          hint: t.overdueCount
              ? `${t.overdueCount} ${plural(t.overdueCount, 'facture a', 'factures ont')} dépassé son délai de paiement`
              : (t.unpaidCount ? `Aucun retard, ${euros(t.unpaidAmount)} en attente de paiement` : 'Aucune facture en attente de paiement'),
        },
        {
          key: 'invoices', to: '/businesses/unpaid', tone: 'warn', done: !t.unsentInvoices,
          value: t.unsentInvoices,
          label: plural(t.unsentInvoices, 'Facture non envoyée', 'Factures non envoyées'),
          hint: t.unsentInvoices ? 'À envoyer à vos clients' : 'Toutes vos factures ont été envoyées',
        },
        {
          key: 'quotes', to: '/businesses/quotes', tone: 'warn', done: !t.unsentQuotes,
          value: t.unsentQuotes,
          label: plural(t.unsentQuotes, 'Devis non envoyé', 'Devis non envoyés'),
          hint: t.unsentQuotes ? 'À finaliser et à envoyer' : 'Tous vos devis ont été envoyés',
        },
        {
          key: 'prospects', to: '/clients/prospection', tone: 'info', done: !t.prospectCount,
          value: t.prospectCount,
          label: plural(t.prospectCount, 'Client en prospection', 'Clients en prospection'),
          hint: t.prospectsToRelaunch
              ? `${t.prospectsToRelaunch} à relancer (sans contact depuis ${t.relaunchDays} jours)`
              : (t.prospectCount ? 'Tous contactés récemment' : 'Aucun prospect en cours'),
        },
      ];
    },
    hasSingleBentoItem() {
      return [
        this.haveNoOption,
        this.haveBusinessOption,
        true,
        this.haveBusinessOption
      ].filter(Boolean).length === 1;
    }
  },
  mounted() {
    useHead({
      title: 'Logiciel de facturation électronique français sécurisé - CRM, prospection et comptabilité',
      meta: [
        {
          name: 'description',
          content: 'Logiciel français de facturation électronique conforme. Gérez vos clients, votre prospection commerciale et votre comptabilité avec une solution sécurisée et simple.'
        }
      ]
    });
    if(this.isConnected && this.haveBusinessOption){
      this.loadLogData();
    }
    if(this.isConnected && this.haveBusinessOption){
      this.loadTodo();
    }
    if(this.isConnected && this.haveStatisticsOption){
      this.loadStats();
    }
  },
  components: {PublicHome, CustomLoader},
  methods: {
    timeAgo,
    startOfDay(date) {
      const d = new Date(date)
      d.setHours(0, 0, 0, 0)
      return d
    },
    isToday(signedAt) {
      const today = this.startOfDay(new Date())
      const date = this.startOfDay(signedAt)

      return date.getTime() === today.getTime()
    },
    logsToday() {
      return this.logData.filter(log => this.isToday(log.signedAt))
    },
    isThisWeekButNotToday(signedAt) {
      const today = this.startOfDay(new Date())
      const date = this.startOfDay(signedAt)

      if (date.getTime() === today.getTime()) {
        return false
      }

      const day = today.getDay() || 7 // dimanche = 7
      const monday = new Date(today)
      monday.setDate(today.getDate() - (day - 1))

      const sunday = new Date(monday)
      sunday.setDate(monday.getDate() + 6)

      return date >= monday && date <= sunday
    },
    logsThisWeek() {
      return this.logData.filter(log =>
          this.isThisWeekButNotToday(log.signedAt)
      )
    },
    isOlderThanThisWeek(signedAt) {
      const today = this.startOfDay(new Date())
      const day = today.getDay() || 7

      const monday = new Date(today)
      monday.setDate(today.getDate() - (day - 1))

      const date = this.startOfDay(signedAt)

      return date < monday
    },
    logsOlder() {
      return this.logData.filter(log =>
          this.isOlderThanThisWeek(log.signedAt)
      )
    },
    // Synthèse des 30 derniers jours, aujourd'hui compris : le back la compare aux 30 jours qui la précèdent (période glissante, pas de mois civil)
    async loadStats(){
      const pad = n => String(n).padStart(2, '0');
      const day = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
      const start = day(first);
      const end = day(now);
      const resp = await StatisticsController.getOverview(`?start=${start}&end=${end}&granularity=month`);
      if(!resp || resp.errors || !resp.kpis){
        this.statsFailed = true;
        return;
      }
      this.stats = resp;
    },
    // Ce qu'il reste à faire : devis / factures non envoyés, impayés échus, prospects
    async loadTodo(){
      const resp = await BusinessController.getTodoSummary();
      if(!resp || resp.errors || resp.overdueCount === undefined){
        this.todoFailed = true;
        return;
      }
      this.todo = resp;
    },
    async loadLogData(){
      this.$refs.loader?.startLoading();
      this.logData = [];
      const resp = await BusinessController.getLogs();
      if(!resp.errors){
        for(let i=0; i < resp.length ;i++){
          this.logData.push(resp[i]);
        }
      }
      this.$refs.loader?.stopLoading();
    }
  }
}
</script>


<style lang="stylus" scoped>
  @require '~/src/common/style/common.styl'

  ul
    padding-left 0

  // Accueil : tuiles plus compactes et trois colonnes sur grand écran
  .bento-container .bento-item
    // Repère du voile de chargement (CustomLoader) : il doit rester dans la tuile
    position relative
    padding 16px 20px
    width unquote("calc(100% - 40px)")
    margin-top 0
    h3
      font-size 1.2em
      margin-bottom 12px

  .bento-item :deep(.loader)
    top 0
    left 0
    border-radius 16px
    box-shadow none

  @media only screen and (min-width: 1200px)
    .bento-container:not(.single-column)
      column-count 3

  // Frise des dernières évolutions
  .change-logs
    position relative
    margin 18px 0 0
    padding-left 22px
    list-style none
    display flex
    flex-direction column
    gap 10px
    &::before
      content ""
      position absolute
      left 5px
      top 6px
      bottom 6px
      width 2px
      background PAGE_RULE
    li
      position relative
      display flex
      flex-direction row
      justify-content space-between
      gap 12px
      &::before
        content ""
        position absolute
        left -22px
        top 4px
        width 12px
        height 12px
        border-radius 50%
        background PAGE_LEMON
        box-shadow 0 0 0 2px THEME_COLOR_PRIMARY
      p
        margin 0
        &:first-child
          font-weight 700
          color FONT_COLOR
        &:last-child
          color CTRL_TEXT_MUTED
          white-space nowrap
          font-size 14px

  .changelog-intro
    margin 0
    line-height 1.5
    color #3f3931

  .global-container-home
    flex 1
    display flex
    flex-direction column
    align-items center
    justify-content center
    .bento
      display flex
      flex-direction row
      .card
        max-width 500px
        border-radius 12px
        box-shadow 0 4px 12px rgba(0, 0, 0, 0.06)
        padding 15px

  .actions-container
    display flex
    flex-wrap wrap
    gap 12px
    .router-link.action-card
      position relative
      flex 1 1 150px
      display flex
      flex-direction column
      align-items flex-start
      gap 14px
      padding 16px
      border 1px solid #F0DE72
      border-radius 12px
      // Fond citron très clair (plus de gris) : l'icône ressort en citron franc
      background-image linear-gradient(160deg, #FFFDEB, #FFF6BF)
      color FONT_COLOR
      font-weight 700
      cursor pointer
      transition background-color .15s ease, border-color .15s ease, transform .12s ease
      p
        margin 0
      .action-icon
        display grid
        place-items center
        width 44px
        height 44px
        border-radius 50%
        background-color PAGE_LEMON
        color THEME_COLOR_PRIMARY
        font-size 18px
        box-shadow 0 4px 10px -4px rgba(214, 170, 0, 0.55)
        transition background-color .15s ease
      &:hover
        background-image linear-gradient(160deg, #FFFFFF, #FFF8C6)
        border-color THEME_COLOR_PRIMARY
        transform translateY(-2px)
      &:active
        transform scale(0.98)

  .action-badge
    position absolute
    top 12px
    right 12px
    display inline-flex
    align-items center
    justify-content center
    min-width 24px
    height 24px
    padding 0 7px
    border-radius 12px
    background #fee2e2
    color #9e2c18
    font-size 13px
    font-weight 700

  .action-links
    display flex
    flex-direction column
    margin 14px -8px 0
    .router-link
      display flex
      align-items center
      justify-content space-between
      height 44px
      padding 0 8px
      border-top 1px solid PAGE_RULE_SOFT
      border-radius 6px
      color THEME_COLOR_PRIMARY
      font-weight 700
      transition background-color .12s ease
      &::after
        content ""
        width 8px
        height 8px
        border-right 2px solid currentColor
        border-top 2px solid currentColor
        transform rotate(45deg)
        margin-right 4px
      &:hover
        background-color PAGE_LEMON_HOVER

  // Tuile « À faire » de l'accueil : une ligne par chose qui attend l'utilisateur
  .todo-sub
    margin -8px 0 14px
    font-size 12.5px
    color CTRL_TEXT_MUTED

  .todo-empty
    padding 18px 0
    color CTRL_TEXT_MUTED

  .todo-list
    display flex
    flex-direction column
    gap 8px
    margin 0
    list-style none

  .router-link.todo-item
    display flex
    align-items center
    gap 14px
    padding 8px 12px
    border 1px solid PAGE_RULE
    border-left-width 4px
    border-radius 12px
    background-color white
    color FONT_COLOR
    transition background-color .15s ease, border-color .15s ease, transform .12s ease
    &:hover
      background-color PAGE_LEMON_HOVER
      transform translateX(2px)
    &.bad
      border-left-color #dc0000
    &.warn
      border-left-color #d58900
    &.info
      border-left-color THEME_COLOR_PRIMARY
    // Rien à faire : la ligne s'efface
    &.is-done
      border-left-color PAGE_RULE
      opacity 0.62
      .todo-value
        color CTRL_TEXT_MUTED
    .todo-value
      flex 0 0 auto
      min-width 48px
      font-size 20px
      line-height 1.1
      color THEME_COLOR_PRIMARY
      font-variant-numeric tabular-nums
    &.bad:not(.is-done) .todo-value
      color #dc0000
    .todo-text
      display flex
      flex-direction column
      min-width 0
    .todo-label
      font-weight 700
    .todo-hint
      font-size 12.5px
      color CTRL_TEXT_MUTED

  // Tuile Statistiques de l'accueil : le mois en cours face au mois précédent
  .stats-sub
    margin -8px 0 18px
    font-size 12.5px
    color CTRL_TEXT_MUTED

  .stats-empty
    padding 18px 0
    color CTRL_TEXT_MUTED

  .stats-grid
    display grid
    grid-template-columns repeat(2, minmax(0, 1fr))
    gap 12px 18px

  .stats-item
    display flex
    flex-direction column
    gap 4px

  .stats-label
    font-size 12.5px
    font-weight 600
    color CTRL_TEXT_MUTED

  .stats-value
    font-size 21px
    line-height 1.1
    color THEME_COLOR_PRIMARY
    font-variant-numeric tabular-nums

  .stats-hint
    font-size 12px
    color CTRL_TEXT_MUTED

  .stats-note
    margin 14px 0 0
    padding 8px 10px
    border-radius 10px
    background #FFFBE0
    border 1px dashed #E9D45A
    font-size 12.5px
    line-height 1.45
    color CTRL_TEXT_MUTED

  .stats-growth
    position relative
    display inline-flex
    align-items center
    align-self flex-start
    height 22px
    padding 0 8px
    border-radius 11px
    font-size 12px
    font-weight 600
    cursor help
    background #f0f1f3
    color #626c82
    &.good
      background #E1F3E6
      color #1f6a2c
    &.bad
      background #FBE4DF
      color #a3283d
    .stats-tip
      position absolute
      left 0
      bottom unquote("calc(100% + 8px)")
      z-index 5
      display none
      padding 7px 11px
      border-radius 8px
      background-color #17233f
      color white
      font-size 12px
      font-weight 600
      white-space nowrap
      &::after
        content ""
        position absolute
        left 14px
        top 100%
        border 6px solid transparent
        border-top-color #17233f
    &:hover .stats-tip
      display block

  .stats-link
    display inline-block
    margin-top 14px
    color THEME_COLOR_PRIMARY
    font-weight 700
    text-decoration underline
    text-decoration-color PAGE_LEMON
    text-decoration-thickness 3px
    text-underline-offset 4px
    &:hover
      background-color PAGE_LEMON_HOVER

  .notification-container
    display flex
    flex-direction column
    // Journal : la liste défile dans la tuile au lieu de l'allonger
    max-height 260px
    overflow-y auto
    .section
      display flex
      flex-direction column
      padding-bottom 10px
      .header-section
        display flex
        font-weight bold
        font-size 16px
        padding-bottom 5px
        color THEME_COLOR_PRIMARY
      .notification
        display flex
        align-items center
        margin 2px
        .signature
          display flex
          background-color INFO
          border-radius 5px
          p
            color white
            margin 5px
            font-weight bold
            font-size 11px
        .number
          padding-right 10px
          margin 0
          font-weight bold
          padding-left 10px
          color THEME_COLOR_PRIMARY
        .signedAt
          padding-right 10px
          margin 0
          color #787b83
          text-align right
        .currentClientName
          margin 0
          font-size 16px
          text-align left
          white-space nowrap
          overflow hidden
          text-overflow ellipsis
          flex 1
</style>